import { getPairedAccount, getQuota, } from "@apps/client-sdk";
import { showConfirmDialog } from "../../confirmDialog.js";
import { createStatusController, el } from "../sieve/dom.js";
function formatKib(kib) {
    if (kib == null || Number.isNaN(kib)) {
        return "—";
    }
    const bytes = kib * 1024;
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    const units = ["KiB", "MiB", "GiB", "TiB"];
    let value = kib;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
    }
    const digits = value >= 100 ? 0 : value >= 10 ? 1 : 2;
    return `${value.toFixed(digits)} ${units[unit]}`;
}
function quotaPercent(used, limit) {
    if (used == null || limit == null || limit <= 0) {
        return null;
    }
    return Math.min(100, Math.round((used / limit) * 1000) / 10);
}
function defaultBeforeDate() {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function renderQuotaPanel(quota, error) {
    const wrap = el("div", "archiving-quota mt");
    wrap.append(el("h3", undefined, "Mailbox quota"));
    if (error) {
        wrap.append(el("p", "flash flash-error", error));
        return wrap;
    }
    if (!quota) {
        wrap.append(el("p", "muted", "Loading quota…"));
        return wrap;
    }
    if (!quota.quotas.length) {
        wrap.append(el("p", "muted", "No quota information returned for this account."));
        return wrap;
    }
    const list = el("div", "archiving-quota-list");
    for (const root of quota.quotas) {
        const row = el("div", "archiving-quota-row");
        row.append(el("div", "archiving-quota-name", root.name));
        const used = formatKib(root.storage_used_kib);
        const limit = root.storage_limit_kib == null || root.storage_limit_kib <= 0
            ? "Unlimited"
            : formatKib(root.storage_limit_kib);
        row.append(el("div", "archiving-quota-values", `${used} / ${limit}`));
        const pct = quotaPercent(root.storage_used_kib, root.storage_limit_kib);
        if (pct != null) {
            const bar = el("div", "archiving-quota-bar");
            const fill = el("div", "archiving-quota-bar-fill");
            fill.style.width = `${pct}%`;
            if (pct >= 90) {
                fill.classList.add("is-critical");
            }
            else if (pct >= 75) {
                fill.classList.add("is-warn");
            }
            bar.append(fill);
            row.append(bar);
            row.append(el("div", "muted archiving-quota-pct", `${pct}% used`));
        }
        list.append(row);
    }
    wrap.append(list);
    return wrap;
}
export function renderArchivingSection(options) {
    const section = el("section", "card archiving-section");
    section.append(el("h2", undefined, "Archiving"));
    const status = el("p", "muted", "");
    status.hidden = true;
    section.append(status);
    const { showStatus } = createStatusController(status);
    const accountLine = el("p", "muted", "Loading account…");
    section.append(accountLine);
    const quotaHost = el("div");
    quotaHost.append(renderQuotaPanel(null, null));
    section.append(quotaHost);
    const canMove = Boolean(options.getArchiveFolderContext && options.runArchive);
    const loadAccountAndQuota = async () => {
        let pairedAccountLabel = null;
        try {
            const account = await getPairedAccount(options.storage);
            pairedAccountLabel = account?.trim() || null;
            accountLine.textContent = pairedAccountLabel
                ? `Active account: ${pairedAccountLabel}`
                : "No paired account.";
        }
        catch {
            pairedAccountLabel = null;
            accountLine.textContent = "No paired account.";
        }
        try {
            const quota = await getQuota(options.storage);
            quotaHost.replaceChildren(renderQuotaPanel(quota, null));
        }
        catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            quotaHost.replaceChildren(renderQuotaPanel(null, message));
        }
        return pairedAccountLabel;
    };
    // PWA / hosts without Local Folders moves: quota + Thunderbird nudge (no move form).
    if (!canMove) {
        section.append(el("p", "muted mt", "Message archiving to Local Folders is available in the Thunderbird Email Apps extension."));
        void loadAccountAndQuota();
        return section;
    }
    const folderLine = el("p", "muted mt", "Detecting selected folder…");
    section.append(folderLine);
    const form = el("div", "archiving-form mt");
    const dateLabel = el("label", "archiving-date-label");
    dateLabel.append(document.createTextNode("Archive messages before"));
    const dateRow = el("div", "archiving-date-row");
    const dateInput = el("input");
    dateInput.type = "date";
    dateInput.className = "archiving-date-input";
    dateInput.value = defaultBeforeDate();
    const countEl = el("span", "archiving-count muted", "");
    dateRow.append(dateInput, countEl);
    dateLabel.append(dateRow);
    form.append(dateLabel);
    const archiveBtn = el("button", "btn mt", "Archive folder");
    archiveBtn.type = "button";
    archiveBtn.disabled = true;
    form.append(archiveBtn);
    section.append(form);
    const destHint = el("p", "muted mt", "");
    section.append(destHint);
    let folderInfo = null;
    let countRequestId = 0;
    const mailboxEmailForArchive = () => {
        const email = folderInfo?.mailboxEmail?.trim().toLowerCase();
        return email && email.includes("@") ? email : null;
    };
    const folderPathLabel = () => {
        if (!folderInfo) {
            return "";
        }
        if (folderInfo.pathParts?.length) {
            return folderInfo.pathParts.join(" / ");
        }
        return folderInfo.name;
    };
    const updateDestHint = () => {
        if (!folderInfo) {
            destHint.textContent = "";
            return;
        }
        const accountPart = mailboxEmailForArchive() || "<mailbox email>";
        destHint.textContent = `Messages will be moved to Local Folders / Archive / ${accountPart} / ${folderPathLabel()}`;
    };
    const setCountText = (text) => {
        countEl.textContent = text;
    };
    const refreshCount = async () => {
        if (!options.countArchiveMessages || !folderInfo?.canArchive) {
            setCountText("");
            return;
        }
        const before = dateInput.value;
        if (!before) {
            setCountText("");
            return;
        }
        const requestId = ++countRequestId;
        setCountText("Counting…");
        try {
            const count = await options.countArchiveMessages(before);
            if (requestId !== countRequestId) {
                return;
            }
            setCountText(count === 1 ? "1 message" : `${count.toLocaleString()} messages`);
        }
        catch {
            if (requestId !== countRequestId) {
                return;
            }
            setCountText("Count unavailable");
        }
    };
    const refreshFolder = async () => {
        if (!options.getArchiveFolderContext) {
            return;
        }
        try {
            folderInfo = await options.getArchiveFolderContext();
        }
        catch (error) {
            folderInfo = null;
            folderLine.textContent =
                error instanceof Error ? error.message : "Could not read the selected folder.";
            archiveBtn.disabled = true;
            destHint.textContent = "";
            setCountText("");
            return;
        }
        if (!folderInfo) {
            folderLine.textContent = "Select a mail folder in Thunderbird, then reopen this section.";
            archiveBtn.disabled = true;
            destHint.textContent = "";
            setCountText("");
            return;
        }
        folderLine.textContent = `Selected folder: ${folderPathLabel()}${folderInfo.path ? ` (${folderInfo.path})` : ""}`;
        updateDestHint();
        archiveBtn.disabled = !folderInfo.canArchive || !mailboxEmailForArchive();
        if (!folderInfo.canArchive && folderInfo.reasonDisabled) {
            showStatus(folderInfo.reasonDisabled, "error");
            setCountText("");
        }
        else {
            void refreshCount();
        }
    };
    const loadQuota = async () => {
        await loadAccountAndQuota();
        updateDestHint();
        if (folderInfo) {
            archiveBtn.disabled = !folderInfo.canArchive || !mailboxEmailForArchive();
        }
    };
    archiveBtn.addEventListener("click", () => {
        void (async () => {
            if (!options.runArchive || !folderInfo?.canArchive) {
                return;
            }
            const before = dateInput.value;
            if (!before) {
                showStatus("Choose a date before which messages should be archived.", "error");
                return;
            }
            const mailboxEmail = mailboxEmailForArchive();
            if (!mailboxEmail) {
                showStatus("Could not find an email address for this mailbox. Check the account’s identity email in Thunderbird.", "error");
                return;
            }
            const destination = `Local Folders / Archive / ${mailboxEmail} / ${folderPathLabel()}`;
            const confirmed = await showConfirmDialog(options.root, {
                title: "Archive messages?",
                message: [
                    `Messages in “${folderPathLabel()}” dated before ${before} will be moved to:`,
                    "",
                    destination,
                    "",
                    "Those messages leave the mailbox folder on the server (and free quota once the server updates).",
                    "",
                    "To undo: open that folder under Local Folders → Archive, select the messages, and move them back to the original mailbox folder.",
                ].join("\n"),
                confirmLabel: "Archive",
                cancelLabel: "Cancel",
                danger: true,
            });
            if (!confirmed) {
                return;
            }
            archiveBtn.disabled = true;
            showStatus("Archiving…", "muted");
            try {
                const result = await options.runArchive({
                    beforeDate: before,
                    accountEmail: mailboxEmail,
                });
                showStatus(result.moved === 0
                    ? `No messages before ${before} in ${result.folderName}.`
                    : `Moved ${result.moved} message${result.moved === 1 ? "" : "s"} to ${result.destinationLabel}.`, result.moved === 0 ? "muted" : "success");
                if (result.moved > 0) {
                    await loadQuota();
                }
                void refreshCount();
            }
            catch (error) {
                showStatus(error instanceof Error ? error.message : String(error), "error");
            }
            finally {
                archiveBtn.disabled = !folderInfo?.canArchive || !mailboxEmailForArchive();
            }
        })();
    });
    dateInput.addEventListener("change", () => {
        void refreshCount();
    });
    dateInput.addEventListener("input", () => {
        void refreshCount();
    });
    void loadQuota();
    void refreshFolder();
    return section;
}
